'use client';

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { PdfThumbnailCaptureHost } from '@/components/ui/PdfThumbnailCaptureHost';
import { parseCitationPageFromMedia, explicitCitationPageFromMedia } from '@/shared/functions';
import type { MediaSource } from '@/shared/types';
import { originBindingFromMedia, resolveUserImageUploadCaseId } from '@/shared/userImageStorage';
import {
  applyPdfThumbnailToMedia,
  eventMediaNeedsThumbnail,
  thumbnailBackfillKey,
  thumbnailMediaKind,
  uploadThumbnail,
} from '@/shared/timelineThumbnails';

const MAX_EVENT_MEDIA_THUMBNAIL_BACKFILLS = 12;

function eventMediaBackfillKey(index: number, media: MediaSource): string {
  return `${thumbnailBackfillKey('event-media', index, media)}:${parseCitationPageFromMedia(media)}`;
}

export type UseEventMediaThumbnailBackfillArgs = {
  media: MediaSource[] | undefined;
  editingEnabled: boolean;
  caseId: string | undefined;
  cellId: string | null;
  accessToken: string | null;
  tenantFriendlyName?: string;
  onPatchMedia: (index: number, next: MediaSource) => void;
};

/**
 * Auto-generate pdf/docx/pptx compact previews for Displayed Media tiles in the
 * event modal. Timeline backfill persists via ``updateEvent``; the unsaved Add
 * New Event row has no equivalent, so this writes the bookmark
 * ``mediaContext.canvas.image`` onto the local media list. It does not write
 * ``mediaContext.capture`` — that field is a user capture, not a tile thumb.
 */
export function useEventMediaThumbnailBackfill(
  args: UseEventMediaThumbnailBackfillArgs,
): { pdfCaptureHost: ReactNode } {
  const { media, editingEnabled, caseId, cellId, accessToken } = args;

  const [pdfJobMedia, setPdfJobMedia] = useState<MediaSource | null>(null);
  const pdfResolverRef = useRef<((dataUrl: string | null) => void) | null>(null);
  const attemptedRef = useRef<Set<string>>(new Set());
  const runGenRef = useRef(0);
  const argsRef = useRef(args);
  argsRef.current = args;

  const capturePdfThumbnail = (item: MediaSource) =>
    new Promise<string | null>((resolve) => {
      let settled = false;
      const finish = (dataUrl: string | null) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeoutId);
        pdfResolverRef.current = null;
        setPdfJobMedia(null);
        resolve(dataUrl);
      };
      const timeoutId = window.setTimeout(() => finish(null), 30_000);
      pdfResolverRef.current = finish;
      setPdfJobMedia(item);
    });

  const mediaLength = media?.length ?? 0;
  const caseIdForAttemptedRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (caseIdForAttemptedRef.current !== caseId) {
      attemptedRef.current = new Set();
      caseIdForAttemptedRef.current = caseId;
    }
  }, [caseId]);

  useEffect(() => {
    const enabled =
      editingEnabled &&
      mediaLength > 0 &&
      !!cellId &&
      !!accessToken;
    if (!enabled) return;

    const gen = ++runGenRef.current;
    const isCurrent = () => runGenRef.current === gen;

    void (async () => {
      let processed = 0;
      while (isCurrent() && processed < MAX_EVENT_MEDIA_THUMBNAIL_BACKFILLS) {
        const latest = argsRef.current;
        const list = latest.media ?? [];
        const pendingIndex = list.findIndex((item, index) => {
          if (thumbnailMediaKind(item) !== 'pdf') return false;
          if (!eventMediaNeedsThumbnail(item)) return false;
          return !attemptedRef.current.has(eventMediaBackfillKey(index, item));
        });
        if (pendingIndex < 0) break;
        const item = list[pendingIndex];
        const key = eventMediaBackfillKey(pendingIndex, item);
        attemptedRef.current.add(key);
        processed += 1;
        try {
          const imageData = await capturePdfThumbnail(item);
          if (!isCurrent() || !imageData) continue;
          const { sourceKey, sourceMd5 } = originBindingFromMedia(item);
          const caseIdForUpload = resolveUserImageUploadCaseId({
            selectedCaseId: latest.caseId,
            media: item,
            sourceKey,
          });
          if (!caseIdForUpload || !latest.cellId || !latest.accessToken) continue;
          const imageRef = await uploadThumbnail({
            imageData,
            purpose: 'pdf-page-thumbnail',
            caseId: caseIdForUpload,
            cellId: latest.cellId,
            accessToken: latest.accessToken,
            tenantFriendlyName: latest.tenantFriendlyName,
            sourceKey,
            sourceMd5,
          });
          if (!isCurrent()) continue;
          const current = (latest.media ?? [])[pendingIndex] ?? item;
          latest.onPatchMedia(
            pendingIndex,
            applyPdfThumbnailToMedia(
              current,
              imageRef,
              explicitCitationPageFromMedia(current),
            ),
          );
        } catch (error) {
          console.warn(
            'Failed to auto-generate event media thumbnail:',
            error instanceof Error ? error.message : error,
          );
        }
      }
    })();

    return () => {
      runGenRef.current += 1;
      pdfResolverRef.current?.(null);
    };
  }, [mediaLength, editingEnabled, caseId, cellId, accessToken]);

  return {
    pdfCaptureHost: (
      <PdfThumbnailCaptureHost
        media={pdfJobMedia}
        onCaptured={(dataUrl) => {
          pdfResolverRef.current?.(dataUrl);
        }}
      />
    ),
  };
}
